/**
 * The Google consent flow leaves the app: we send the admin to Google, and Google sends them back
 * to our callback page. What the callback needs to finish the flow is kept in sessionStorage, which
 * survives that round trip in the same tab and nowhere else.
 */
const STORAGE_KEY = 'google-idp-pending-flow';

export type PendingGoogleIdPFlow = {
  /** Returned by start, and required by verify. It is also the OAuth `state`. */
  requestToken: string;
  /** The provider page the admin started from, to return to if verify fails. */
  providerId: string;
};

export function savePendingGoogleIdPFlow(flow: PendingGoogleIdPFlow) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(flow));
}

/** Reads and removes the pending flow. It is one-shot, like the request token itself. */
export function takePendingGoogleIdPFlow(): PendingGoogleIdPFlow | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'requestToken' in parsed &&
      'providerId' in parsed &&
      typeof parsed.requestToken === 'string' &&
      typeof parsed.providerId === 'string'
    ) {
      return { requestToken: parsed.requestToken, providerId: parsed.providerId };
    }
  } catch {
    // Unreadable storage is the same as no pending flow.
  }
  return null;
}
